/**
 * Browser entry point: config, auth, session bootstrap, and the React root.
 *
 * Routing is deliberately tiny — three cases, no router library:
 *   `/auth/callback`  completes the WorkOS redirect (only if that flow is used)
 *   anonymous         the sign-in card
 *   anything else     the workspace
 *
 * The auth client is kept outside React and observed through a subscription
 * rather than mirrored into state. It has its own lifecycle (a refresh can be
 * rejected while the relay socket is perfectly healthy), and mirroring it would
 * mean two sources of truth for whether there is a session.
 */

import { StrictMode, useSyncExternalStore } from "react";
import { createRoot } from "react-dom/client";
import { AuthClient, cryptoRandomBytes, type AuthStorage } from "../lib/auth";
import { ZeronSession } from "../lib/session";
import { AppStore } from "../app/store";
import { App } from "../react/app";
import { LoginScreen } from "../react/login";
import { StoreProvider } from "../react/store-context";

interface Bootstrap {
  edgeUrl: string;
  /** `device` = production device grant; `dev` = local only. */
  authMode: "device" | "dev";
}

const bootstrap: Bootstrap = {
  edgeUrl: "https://edge.zeron.sh",
  authMode: "device"
};

/**
 * Runtime config from the BFF.
 *
 * Nothing secret is in here, but it still comes from the server so the HTML stays
 * a static asset with no inline script — which is what lets the CSP forbid
 * `'unsafe-inline'`.
 *
 * The WorkOS client id is deliberately not sent: the device grant is driven
 * entirely by the BFF, so the browser never needs to name the client. The
 * redirect flow, which does need it, remains in `src/lib/auth.ts` for deployments
 * that own the WorkOS project and can register a redirect URI.
 */
const loadConfig = async (): Promise<void> => {
  try {
    const response = await fetch("/api/config", { credentials: "same-origin" });
    if (!response.ok) return;
    const config = (await response.json()) as Partial<Bootstrap>;
    if (typeof config.edgeUrl === "string") bootstrap.edgeUrl = config.edgeUrl;
    if (config.authMode === "dev" || config.authMode === "device") {
      bootstrap.authMode = config.authMode;
    }
  } catch {
    // Keep the defaults: a login attempt will fail with a clear message.
  }
};

/**
 * `sessionStorage` for the PKCE pair — deliberately not `localStorage`, so a
 * second tab cannot complete a login it did not start, and the verifier dies
 * with the tab.
 */
const sessionStorageAdapter = (): AuthStorage => {
  try {
    const storage = globalThis.sessionStorage;
    if (storage === undefined) throw new Error("no sessionStorage");
    return {
      get: (key) => storage.getItem(key),
      set: (key, value) => storage.setItem(key, value),
      remove: (key) => storage.removeItem(key)
    };
  } catch {
    const map = new Map<string, string>();
    return {
      get: (key: string) => map.get(key) ?? null,
      set: (key: string, value: string) => void map.set(key, value),
      remove: (key: string) => void map.delete(key)
    };
  }
};

const auth = new AuthClient({
  fetch: (input, init) => fetch(input, init),
  storage: sessionStorageAdapter(),
  now: () => Date.now(),
  randomBytes: cryptoRandomBytes
});

/** The session and store, built once. `start()` is idempotent so a re-render
 * cannot dial a second socket. */
let started = false;
let store: AppStore | undefined;

const ensureStarted = (): AppStore => {
  if (store === undefined) {
    const session = new ZeronSession({ edgeUrl: bootstrap.edgeUrl, auth });
    store = new AppStore({ session, auth });
  }
  if (!started) {
    started = true;
    void store.start();
  }
  return store;
};

/**
 * Root.
 *
 * `useSyncExternalStore` on the auth client rather than `useState` + `useEffect`:
 * the client can transition during a render (a refresh rejection arriving in the
 * same tick as an action), and an effect-based subscription would miss it and
 * leave the user on a workspace that silently fails every call.
 */
function Root() {
  const authState = useSyncExternalStore(
    (listener) => auth.subscribe(listener),
    () => auth.current
  );

  if (authState.status === "expired") {
    return (
      <LoginScreen auth={auth} authMode={bootstrap.authMode} />
    );
  }
  if (authState.status !== "authenticated") {
    return <LoginScreen auth={auth} authMode={bootstrap.authMode} />;
  }
  return (
    <StoreProvider store={ensureStarted()}>
      <App />
    </StoreProvider>
  );
}

/**
 * Handle `/auth/callback?code=…&state=…`.
 *
 * Replaced in the URL first, so a refresh does not replay the failed callback.
 */
const completeCallback = async (): Promise<boolean> => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const denied = params.get("error");
  if (denied === null && (code === null || state === null)) return false;
  window.history.replaceState(null, "", "/");
  if (denied !== null) return true;
  try {
    await auth.completeLogin(code as string, state as string);
  } catch {
    // The auth client records the failure in its own state, which the root
    // renders as the sign-in card.
  }
  return true;
};

const main = async (): Promise<void> => {
  await loadConfig();
  if (window.location.pathname === "/auth/callback") {
    const handled = await completeCallback();
    if (handled) {
      // `completeLogin` already updated the auth state; the root will render the
      // workspace on the next tick.
    }
  }
  await auth.restore();
  const container = document.getElementById("app");
  if (container === null) throw new Error("#app missing from index.html");
  createRoot(container).render(
    <StrictMode>
      <Root />
    </StrictMode>
  );
};

void main();
